import { datadogRum } from '@datadog/browser-rum';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js/pure';
import * as _ from 'lodash';
import { Component } from 'react';
import type { Stripe } from '@stripe/stripe-js';

import ApplicationMobileWarning from './components/ApplicationMobileWarning';
import DialogManager from './components/DialogManager';
import StatusBanner from './components/generic/StatusBanner';
import ModalPlanSelector from './components/modals/ModalPlanSelector';
import ModalPostUpgrade from './components/modals/ModalPostUpgrade';
import JobNotificationProvider from './components/JobNotificationHub/Provider';
import Toaster from './components/Toaster/Toaster';
import DialogContextProvider from './contexts/DialogContext';
import ToastContextProvider from './contexts/ToastContext';
import { window } from './globals';
import * as utils from './utils';
import type { MessageOptions } from './components/modals/ModalPlanSelector';
import RouterContextProvider from './contexts/RouterContext';
import { Provider as PortalUrlProvider } from './components/shared/publicPortal/context/portalUrlContext';

type PlanSelectorModalProps = {
  show: boolean;
  messageOptions?: MessageOptions & {
    onHide?: () => void;
  };
};

/* eslint-disable @typescript-eslint/naming-convention */
type ApplicationState = {
  plan_selector_modal_props: PlanSelectorModalProps;
};
/* eslint-enable @typescript-eslint/naming-convention */

//  Loads the stripe.js script tag asynchronously
//  stripePromise is passed to Elements in cloud envs where a stripe_key is present
const stripePromise = window.stripe_key && !window.on_prem
  ? loadStripe(window.stripe_key)
  : null as Stripe;

// Root application component
class Application extends Component<any, ApplicationState> {
  constructor(props) {
    super(props);
    this.state = {
      plan_selector_modal_props: {
        show: false,
      },
    };
  }

  componentDidMount() {
    this.enableDatadogRUM();
    this.identifyHeapUser();
    this.enableDevrev();

    window.showPlanSelectorModal = (messageOptions?: MessageOptions) =>
      this.setState({ plan_selector_modal_props: { show: true, messageOptions } });

    utils.showDelightedSurveyAfter24Hours();
  }

  componentWillUnmount() {
    delete window.showPlanSelectorModal;
  }

  // Send user id to heap, so we can use it to identify the user in the heap dashboard.
  identifyHeapUser() {
    if (window.heap && window.user?.id) {
      window.heap.identify(window.user.id.toString());
      window.heap.addUserProperties({
        email: window.user.email,
        createdAt: window.user.createdAt,
        organizationId: window.user.organizationId,
        organization: window.user.organization?.title,
      });
    }
  }

  enableDatadogRUM() {
    if (!utils.isTrue(window.datadogRUM?.enabled)) {
      return;
    }

    datadogRum.init({
      version: window.fossa_version,
      ...window.datadogRUM,
    });

    if (window.user) {
      datadogRum.setUser({
        id: `${window.user.id}`,
        name: window.user.username,
        email: window.user.email,
        organizationId: window.user.organizationId,
        organizationTitle: window.user.organization?.title,
      });
    }
  }

  enableDevrev() {
    if (!window.devrev.appId || !window.plugSDK) {
      return;
    }

    window.plugSDK.init({
      app_id: window.devrev.appId,
      session_token: window.devrev.sessionToken,
    });
  }

  renderBody = () => (
    <PortalUrlProvider>
      <DialogContextProvider>
        <ToastContextProvider>
          <RouterContextProvider router={this.props.router} location={this.props.location}>
            <JobNotificationProvider>
              {utils.isAuthed() && (
                <ApplicationMobileWarning />
              )}
              <StatusBanner />
              {this.props.children}
              {!window.on_prem && (
              <>
                <ModalPlanSelector
                  isShowing={this.state.plan_selector_modal_props.show}
                  onHide={() => {
                    const customOnHide = this.state.plan_selector_modal_props?.messageOptions?.onHide || _.noop;
                    customOnHide();
                    this.setState({
                      plan_selector_modal_props: {
                        show: false,
                        // Explicitly make these undefined to reset.
                        messageOptions: undefined,
                      },
                    });
                  }}
                  messageOptions={_.omit(this.state.plan_selector_modal_props.messageOptions, 'onHide')}
                    />
                <ModalPostUpgrade />
              </>
              )}
              <DialogManager />
              <Toaster location={this.props.location} />
            </JobNotificationProvider>
          </RouterContextProvider>
        </ToastContextProvider>
      </DialogContextProvider>
    </PortalUrlProvider>
  );

  render() {
    if (window.stripe_key) {
      return (
        <Elements stripe={stripePromise}>
          {this.renderBody()}
        </Elements>
      );
    }
    return this.renderBody();
  }
}

export default Application;
